import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FlexAlignCenter, FlexAlignCenterSpaceBetween } from 'components/primitives/flex';
import IconCaretDown from 'assets/scss/fortawesome/icons/svg/caret-down.svg';
import { useMutation } from 'react-query';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import OpenApiConfiguration from 'api/OpenApiConfiguration';
import { DomesticV2ConversationsConversationIdAdminTrackConversationPostRequest } from 'swagger/models';
import { ConversationsApi } from 'swagger/apis/conversations-api';
import { useUserDetails } from 'auth/mutations/userHooks';
import RadioButton from 'components/ui/RadioButton/RadioButton';
import PopOver from 'components/ui/PopOver/PopOver';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import useConversationDetail from './useConversationDetail';
import { activeConversationIdAtom } from '../store';

const conversationsApi = new ConversationsApi(OpenApiConfiguration);

export const useShowAdminView = () => {
  // returns true if we want to show the admin view
  // showing admin view basically means that the user is an admin and is not impersonating (viewing another user's inbox)
  // in admin view we don't show the conversation tabs content so there are no calls to the backend
  const { data, isSuccess, isIdle } = useConversationDetail();
  const router = useRouter();

  const { userDetails } = useUserDetails();
  const isUserAdmin = userDetails?.is_admin ?? false;
  const isUserImpersonating = userDetails?.is_impersonating ?? false;

  const showAdminView = checkIfShowAdminView(isUserAdmin, data);

  const [firstConversationDetailHasLoaded, setFirstConversationDetailHasLoaded] = useState(false);

  useEffect(() => {
    // because isSuccess changes from true to false when clicking on a conversation item in the conversation tab, we need to capture the first time it becomes true
    // which means that the conversation detail data has been fetched and we can check if my_user_type_in_conversation equals admin
    // so we introduce firstSuccessPassed state variable to capture the first time isSuccess becomes true
    if (isSuccess && !firstConversationDetailHasLoaded) {
      setFirstConversationDetailHasLoaded(true);
    }
  }, [isSuccess, firstConversationDetailHasLoaded]);

  if (!router.isReady) {
    // the router is not ready yet, so we don't know if there is a conversation id in the url
    // so we return true to disable the tabs content loading
    return true;
  }
  if (!isUserAdmin) {
    // if the user is not an admin, we don't show the admin view
    return false;
  }
  if (isUserImpersonating) {
    // if the user is impersonating, we don't show the admin view
    return false;
  }
  if (isIdle) {
    // if the query is idle, there is no conversation id in the url, so we know we don't need to show the admin view (the admin is viewing his own inbox)
    // so we return false to enable the tabs content loading
    return false;
  }
  if (!firstConversationDetailHasLoaded) {
    // we still don't have the conversation details, so we don't know if the my_user_type_in_conversation equals admin or not
    // so we return true to disable the tabs content loading
    return true;
  }
  return showAdminView;
};

export const useAdminMessageRecipientSelect = () => {
  const {
    data: { booking, admin_tracking },
  } = useConversationDetail();
  const activeConversationId = useAtomValue(activeConversationIdAtom);
  const adminDropdownItems = getAdminDropdownItems(booking);
  const [adminMessageRecipient, setAdminMessageRecipient] = useState(adminDropdownItems.all.value);
  const [adminTabOpen, setAdminTabOpen] = useState(false);
  const [trackingConvo, setTrackingConvo] = useState(admin_tracking);
  const trackConversationMutation = useMutation(
    ({
      conversationId,
      ...body
    }: {
      conversationId: string;
    } & DomesticV2ConversationsConversationIdAdminTrackConversationPostRequest) =>
      conversationsApi.domesticV2ConversationsConversationIdAdminTrackConversationPost(conversationId, body)
  );

  const showAdminView = useShowAdminView();

  useEffect(() => {
    setTrackingConvo(admin_tracking);
  }, [admin_tracking]);

  const toggleTrackConversation = () => {
    setTrackingConvo(!trackingConvo);
    trackConversationMutation.mutate({ conversationId: activeConversationId, unsubscribe: trackingConvo });
  };

  const adminMessageRecipientSelect = showAdminView ? (
    <PopoverContainer>
      <PopOver
        PanelContents={() => (
          <div>
            <RadioButton
              selected={adminMessageRecipient}
              handleChange={(v) => {
                setAdminMessageRecipient(v);
                setAdminTabOpen(false);
              }}
              items={Object.values(adminDropdownItems)}
            />
            <Divider />
            <FlexAlignCenter mt="1em">
              <Checkbox
                defaultChecked={trackingConvo}
                name="track_conversation"
                id="track_conversation"
                label="Follow this conversation"
                onChange={() => {
                  toggleTrackConversation();
                }}
                bold
              />
            </FlexAlignCenter>
          </div>
        )}
        ToggleComponent={({ onClick }) => (
          <ToggleAdminButton as="button" type="button" onClick={onClick} isOpen={adminTabOpen}>
            <span>{adminDropdownItems[adminMessageRecipient].shortLabel}</span>
            <StyledIconCaretDown width={16} />
          </ToggleAdminButton>
        )}
        isOpenControlled={adminTabOpen}
        setIsOpenControlled={setAdminTabOpen}
        controlled
      />
    </PopoverContainer>
  ) : null;

  return { adminMessageRecipientSelect, adminMessageRecipient };
};

const checkIfShowAdminView = (is_admin, convo) => {
  if (typeof convo === 'undefined') {
    return false;
  }
  if (is_admin && convo.my_user_type_in_conversation === 'admin') {
    return true;
  }
  return false;
};

const getAdminDropdownItems = (booking) => {
  const items = {
    all: {
      label: 'All parties',
      value: 'all',
      shortLabel: 'All parties',
    },
    owner: {
      label: `Owner only - ${booking?.owner?.first_name} ${booking?.owner?.last_name}`,
      value: 'owner',
      shortLabel: 'Owner only',
    },
    renter: {
      label: `Renter only - ${booking?.renter?.first_name} ${booking?.renter?.last_name}`,
      value: 'renter',
      shortLabel: 'Renter only',
    },
    ...(booking?.captain
      ? {
          captain: {
            label: `Captain only - ${booking?.captain?.first_name} ${booking?.captain?.last_name}`,
            value: 'captain',
            shortLabel: 'Captain only',
          },
        }
      : {}),
  };
  if (booking?.captain) {
    return items;
  }
  const { ...other } = items;
  return other;
};

const PopoverContainer = styled.div`
  width: auto !important;
  margin-right: 10px;
`;

const Divider = styled.hr`
  border-style: solid;
  border-width: 1px 0 0 0;
  border-color: #dbdfe5;
  margin: 0;
`;

const StyledIconCaretDown = styled(IconCaretDown)`
  fill: #0751c2;
`;

const ToggleAdminButton = styled(FlexAlignCenterSpaceBetween)<{ isOpen: boolean }>`
  min-width: 150px;
  border-radius: 8px;
  height: 100%;
  padding: 8px 16px;
  border: 1px solid ${({ isOpen }) => (isOpen ? '#8e9697' : '#dbdfe5')};

  ${StyledIconCaretDown} {
    transform: rotate(${({ isOpen }) => (isOpen ? '180deg' : '0deg')});
  }
`;
